import cx from 'classnames'
import * as React from 'react'
import { useEffect, useRef, useState } from 'react'

import type { LinkViewModel } from '../../view-models/LinkViewModel'
import type { GtagEvent } from '../../utils/gtag'
import type { ButtonState } from '../BaseButton/BaseButton'
import BaseButton from '../BaseButton/BaseButton'
import Icon from '../Icon/Icon'
import styles from './button-style.css'

// Shared icon props interface
export interface IconProps {
  /**
   * Icon to display on the left side of the button text. intsert svg
   */
  iconLeft?: string

  /**
   * Icon to display on the right side of the button text
   * insert svg
   */
  iconRight?: string
}

export interface BaseButtonProps {
  /**
   * The content of the button
   */
  children?: React.ReactNode

  /**
   * Determines the visual style of the button
   * @default ButtonTypes.primary
   */
  variant?: 'primary' | 'secondary'
  /**
   * Makes the button smaller
   * @default false
   */
  small?: boolean

  /**
   * Disables the button, preventing user interaction
   * @default false
   */
  disabled?: boolean

  /**
   * Makes the button appear inactive but still clickable
   * @default false
   */
  inactive?: boolean

  /**
   * Click handler for the button
   */
  onClick?: (...args: Array<any>) => any

  /**
   * Additional CSS class names
   */
  className?: string

  /**
   * Optimizes the button for display on dark backgrounds
   * @default false
   */
  darkBg?: boolean

  /**
   * The button type attribute
   * @default 'button'
   */
  type?: 'button' | 'submit' | 'reset' | undefined

  /**
   * event metadata
   */
  eventMeta?: GtagEvent
}

export interface ButtonProps extends BaseButtonProps, IconProps {}

export interface LinkButtonProps
  extends LinkViewModel,
    Omit<BaseButtonProps, 'type' | 'variant'>,
    IconProps {
  variant?: 'primary' | 'secondary' | 'link'
  /**
   * Whether to show an arrow after the link text
   * @default true
   */
  withArrow?: boolean
}

/**
 * Button component
 *
 * A versatile button component that can be styled as primary or secondary.
 *
 * @example
 * ```tsx
 * // Primary button
 * <Button>Click me</Button>
 *
 * // Secondary button with left icon
 * <Button variant="secondary"">User Profile</Button>
 *
 * // Disabled button
 * <Button disabled>Cannot click</Button>
 * ```
 */
export function Button({
  iconLeft,
  iconRight,
  variant = 'primary',
  children,
  onClick,
  disabled,
  inactive,
  small,
  className,
  darkBg,
  ...rest
}: ButtonProps) {
  const [hovering, setHovering] = useState(false)
  const contentRef = useRef<HTMLDivElement>(null)
  const [isIconOnly, setIsIconOnly] = useState(false)

  // Handle interaction state
  const handleInteraction = (state: ButtonState) => {
    setHovering(state.hover || state.focus || state.touch || false)
  }

  // Determine if this is an icon-only button by checking content
  useEffect(() => {
    if (contentRef.current) {
      const svgElements = contentRef.current.querySelectorAll('svg')
      const hasTextContent = !!contentRef.current.textContent?.trim()
      setIsIconOnly(svgElements.length === 1 && !hasTextContent && !children)
    }
  }, [children, iconLeft, iconRight])

  return (
    <BaseButton
      onClick={!inactive ? onClick : undefined}
      onInteraction={!disabled ? handleInteraction : undefined}
      disabled={disabled}
      className={cx(
        styles.new,
        darkBg && styles.darkBg,
        variant ? styles[variant] : styles.primary,
        isIconOnly && styles.iconOnly,
        small && styles.small,
        (!!iconLeft || (!!iconRight && !isIconOnly)) && styles.hasIcon,
        hovering && !inactive ? styles.hovering : null,
        className,
      )}
      {...rest}
    >
      <div ref={contentRef} className={styles.contentWrapper}>
        {iconLeft && (
          <Icon
            iconSrc={iconLeft}
            className={cx([styles.iconLeft, styles.icon])}
          />
        )}

        {children && (
          <span
            className={cx(styles.inner, {
              [styles.hasIconLeft]: iconLeft,
              [styles.hasIconRight]: iconRight,
            })}
          >
            {children}
          </span>
        )}

        {iconRight && (
          <Icon
            iconSrc={iconRight}
            className={cx([styles.iconRight, styles.icon])}
          />
        )}
      </div>
    </BaseButton>
  )
}

/**
 * LinkButton component
 *
 * A component that looks like a button but renders as an anchor tag.
 * Can be styled as primary or secondary and includes an optional arrow.
 *
 * @example
 * ```tsx
 * // Primary link button
 * <LinkButton href="/signup">Sign Up</LinkButton>
 *
 * // Secondary link button with arrow
 * <LinkButton href="/learn-more" variant="secondary">Learn More</LinkButton>
 * ```
 */
export function LinkButton({
  variant = 'primary',
  children,
  href,
  onClick,
  disabled,
  small,
  className,
  darkBg,
  withArrow = true, // Changed default to true
  iconLeft,
  iconRight,
  ...rest
}: LinkButtonProps) {
  const [hovering, setHovering] = useState(false)
  const contentRef = useRef<HTMLDivElement>(null)
  const [isIconOnly, setIsIconOnly] = useState(false)

  // Handle interaction state
  const handleInteraction = (state: ButtonState) => {
    setHovering(state.hover || state.focus || state.touch || false)
  }

  // Determine if this is an icon-only button by checking content
  useEffect(() => {
    if (contentRef.current) {
      const svgElements = contentRef.current.querySelectorAll('svg')
      const hasTextContent = !!contentRef.current.textContent?.trim()
      setIsIconOnly(svgElements.length === 1 && !hasTextContent && !children)
    }
  }, [children, iconLeft, iconRight])

  return (
    <BaseButton
      href={href}
      disabled={disabled}
      isAnchor={!disabled}
      onClick={onClick}
      onInteraction={!disabled ? handleInteraction : undefined}
      className={cx(
        styles.new,
        darkBg && styles.darkBg,
        variant ? styles[variant] : styles.primary,
        isIconOnly && styles.iconOnly,
        small && styles.small,
        (!!iconLeft || (!!iconRight && !isIconOnly)) && styles.hasIcon,
        hovering ? styles.hovering : null,
        className,
      )}
      {...rest}
    >
      <div ref={contentRef} className={styles.contentWrapper}>
        {iconLeft && !withArrow && (
          <Icon
            iconSrc={iconLeft}
            className={cx([styles.iconLeft, styles.icon])}
          />
        )}

        {children && (
          <span
            className={cx(styles.inner, {
              [styles.hasIconLeft]: iconLeft,
              [styles.hasIconRight]: iconRight,
            })}
          >
            {children}
          </span>
        )}

        {iconRight && !withArrow && (
          <Icon
            iconSrc={iconRight}
            className={cx([styles.iconRight, styles.icon])}
          />
        )}

        {withArrow && (
          <div className={styles.arrowHolder}>
            <Icon iconName="ArrowRight" className={styles.arrow} />
            <Icon iconName="ArrowRight" className={styles.arrow} />
          </div>
        )}
      </div>
    </BaseButton>
  )
}
